import { defineStore } from 'pinia'
import { Ref, ref } from 'vue'


import { ChatFile } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { fullTrim } from '@/shared/utils/full-trim'
import { Optional } from '@/shared/utils/optional'

import { useDraftAPILocal } from '../api/draftAPILocals'
import { transformDraftMessageToLocalDraft } from '../lib/transformDraftMessageToLocalDraft'

import { DraftMessage } from './types'

export const useDraftMessageStore = defineStore('draft-messages', () => {
  const drafts: Ref<Map<DraftMessage['pServed'], DraftMessage>> = ref(new Map())
  const useDraftAPI = useDraftAPILocal()

  const findByPServed = (pServed: Room['pServedUser']): Optional<DraftMessage> => {
    const data = useDraftAPI.getItem(pServed)
    if (data && !drafts.value.get(pServed)) {
      useDraftAPI.getItemToDraftMessage(pServed)?.then((result) => {
        drafts.value.set(pServed, result)
      })
    }

    return Optional.ofNullable(drafts.value.get(pServed))
  }

  const addDraft = (pServed: Room['pServedUser'], payload: Omit<DraftMessage, 'timestamp'>) => {
    const { messageText, quotedEventId, files } = payload
    const draftIsNotEmpty = messageText?.length || quotedEventId || files?.length
    if (draftIsNotEmpty) {
      useDraftAPI.setItem(pServed, transformDraftMessageToLocalDraft({
        ...payload,
        timestamp: Date.now() / 1000,
      }))

      drafts.value.set(pServed, {
        ...payload,
        timestamp: Date.now() / 1000,
      })
    }
  }

  const updateDraft = (pServed: Room['pServedUser'], payload: Omit<DraftMessage, 'timestamp'>) => {
    const { files, messageText, quotedEventId, quotedText } = payload
    const draftIsNotEmpty = messageText?.length || quotedEventId || files?.length
    if (draftIsNotEmpty) {
      if (useDraftAPI.getItem(pServed)) {
        drafts.value.set(pServed, {
          files: files!,
          messageText: messageText!,
          quotedEventId: quotedEventId!,
          quotedText: quotedText!,
          timestamp: Date.now() / 1000,
          pServed: pServed!,
        })
        useDraftAPI.setItem(pServed, transformDraftMessageToLocalDraft({
          files: files!,
          messageText: messageText!,
          quotedEventId: quotedEventId!,
          quotedText: quotedText!,
          timestamp: Date.now() / 1000,
          pServed: pServed!,
        }))
      } else {
        addDraft(pServed, payload)
      }
    } else {
      removeDraft(pServed)
    }
  }

  const removeDraft = (pServed: Room['pServedUser']) => {
    drafts.value.delete(pServed)
    useDraftAPI.removeItem(pServed)
  }

  const setDraftText = (pServed: string, messageText: DraftMessage['messageText'] = '') => {
    const text = fullTrim(messageText.trim())
    findByPServed(pServed).ifPresentOrElse(
      ({ files, quotedEventId, quotedText }) => {
        updateDraft(
          pServed,
          {
            pServed,
            messageText: text,
            files: files!,
            quotedEventId: quotedEventId!,
            quotedText: quotedText!,
          },
        )
      },
      () => addDraft(pServed, {
        pServed,
        messageText: text,
      }),
    )
  }

  const setDraftQuote = (pServed: string, quotedEventId: DraftMessage['quotedEventId'] = undefined, quotedText = '') => {
    findByPServed(pServed).ifPresentOrElse(
      ({ files, messageText }) => {
        updateDraft(
          pServed,
          {
            pServed,
            messageText: messageText!,
            files: files!,
            quotedEventId,
            quotedText,
          },
        )
      },
      () => addDraft(pServed, {
        pServed,
        quotedEventId,
        quotedText,
      }),
    )
  }

  const deleteDraftFile = (pServed: string, file: ChatFile) => {
    findByPServed(pServed).ifPresent((draft) => {
      draft.files = draft.files!.filter(f => f.file !== file.file)
    })
  }

  const setDraftFiles = (pServed: string, files: DraftMessage['files'] = []) => {
    findByPServed(pServed).ifPresentOrElse(
      ({ messageText, quotedEventId, quotedText }) => {
        updateDraft(
          pServed,
          {
            pServed,
            messageText: messageText!,
            files,
            quotedEventId,
            quotedText: quotedText!,
          },
        )
      },
      () => addDraft(pServed, {
        pServed,
        files,
      }),
    )
  }

  return {
    findByPServed,
    removeDraft,
    setDraftQuote,
    deleteDraftFile,
    setDraftFiles,
    setDraftText,
  }
})
